import React, { useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import * as Yup from 'yup';

import { GlobalAccountRoleType, PersonOrgType } from '@ndvr/customer-api';

import { FormSheet } from '@/components/FormSheet';
import { usePersonSelector } from 'modules';
import { HOUSEHOLD_ROLE_OPTIONS, useRequiredParams } from 'utils';
import { usePersonCoreDataForTenant } from 'views/person/person-queries';
import { FormInput, FormSection, FormSelect, requiredStringValidator, Text, VerticalLayout } from 'widgets';

import {
    useAddPersonToHouseholdWithRole,
    useCommandCenterHouseholdProfile,
    useGlobalAccount,
} from './household-queries';

const LINK_COPY: Partial<
    Record<PersonOrgType, { title: string; section: string; placeholder: string; error: string }>
> = {
    [PersonOrgType.PERSON]: {
        title: 'Link person to account',
        section: 'Select Person to Link to Account',
        placeholder: 'Select person',
        error: 'Please select a person to link',
    },
    [PersonOrgType.TRUST]: {
        title: 'Link trust to account',
        section: 'Select Trust to Link to Account',
        placeholder: 'Select trust',
        error: 'Please select a trust to link',
    },
    [PersonOrgType.ORGANIZATION]: {
        title: 'Link entity to account',
        section: 'Select Entity to Link to Account',
        placeholder: 'Select entity',
        error: 'Please select an entity to link',
    },
    [PersonOrgType.JOINT_PERSONS]: {
        title: 'Link joint to account',
        section: 'Select Joint to Link to Account',
        placeholder: 'Select joint',
        error: 'Please select a joint to link',
    },
};

export const linkPersonToHouseholdRoleFormSchema = Yup.object().shape({
    accountRoleType: requiredStringValidator.label('Role'),
    accountRoleLabel: Yup.string()
        .max(50)
        .label('Description')
        .when('accountRoleType', {
            is: 'Custom',
            then: (schema) => schema.required('Description is required'),
            otherwise: (schema) => schema.notRequired(),
        }),
});

const linkPersonToHouseholdFormSchema = (selectionError: string) =>
    Yup.object()
        .shape({
            personOrgGUID: Yup.string().required(selectionError),
        })
        .concat(linkPersonToHouseholdRoleFormSchema);

export const LinkPersonDrawer = ({
    onClose,
    personOrgType = PersonOrgType.PERSON,
}: {
    onClose: () => void;
    personOrgType?: PersonOrgType;
}) => {
    const { globalAccountGUID } = useRequiredParams(['globalAccountGUID']);
    const { mutate: addPersonToHouseholdWithRole, isPending } = useAddPersonToHouseholdWithRole();
    const copy = LINK_COPY[personOrgType] ?? LINK_COPY[PersonOrgType.PERSON]!;

    const onSubmit = (data: any, close: () => void) => {
        const roleLabel = data.accountRoleType === 'Custom' ? data.accountRoleLabel : data.accountRoleType;

        addPersonToHouseholdWithRole(
            {
                globalAccountGUID,
                personOrgGUID: data.personOrgGUID,
                roleType: GlobalAccountRoleType.SECONDARY_USER,
                roleLabel,
            },
            { onSuccess: close },
        );
    };

    return (
        <FormSheet
            title={copy.title}
            onClose={onClose}
            onSubmit={onSubmit}
            formSchema={linkPersonToHouseholdFormSchema(copy.error)}
            pending={isPending}
            submitLabel="Link"
        >
            <LinkPersonToHouseholdFormFields personOrgType={personOrgType} />
        </FormSheet>
    );
};

export const LinkPersonToHouseholdRoleFields = () => {
    const { watch } = useFormContext();
    const roleType = watch('accountRoleType');

    return (
        <FormSection title="Assigned Role">
            <VerticalLayout $gap={16}>
                <FormSelect name="accountRoleType" label="Role">
                    <option></option>
                    {HOUSEHOLD_ROLE_OPTIONS.map((role) => (
                        <option key={role}>{role}</option>
                    ))}
                    <option>Custom</option>
                </FormSelect>

                {roleType === 'Custom' ? (
                    <FormInput name="accountRoleLabel" placeholder="Accountant" label="Description" optional />
                ) : null}
            </VerticalLayout>
        </FormSection>
    );
};

const LinkPersonToHouseholdFormFields = ({ personOrgType }: { personOrgType: PersonOrgType }) => {
    const { globalAccountGUID } = useRequiredParams(['globalAccountGUID']);
    const { data: globalAccount, isLoading: isLoadingGlobalAccount } = useGlobalAccount(globalAccountGUID);
    const { data: accountProfile } = useCommandCenterHouseholdProfile(globalAccountGUID);
    const copy = LINK_COPY[personOrgType] ?? LINK_COPY[PersonOrgType.PERSON]!;
    const {
        register,
        setValue,
        formState: { errors },
    } = useFormContext();
    register('personOrgGUID');

    const { data: appUsers, isLoading: isLoadingAppUsers } = usePersonCoreDataForTenant(globalAccount?.tenantGUID);
    const linkableProfiles = useMemo(
        () => appUsers?.filter((profile) => profile.personOrgType === personOrgType),
        [appUsers, personOrgType],
    );
    const householdMembers = useMemo(
        () =>
            accountProfile?.personOrgFinancialProfiles.map(({ profile }) => ({
                personOrgGUID: profile.personOrgGUID,
            })) ?? [],
        [accountProfile],
    );
    const { PersonSelector, selectedPersonProfile } = usePersonSelector({
        profiles: linkableProfiles,
        isLoading: isLoadingGlobalAccount || isLoadingAppUsers,
        excludeItems: householdMembers,
        placeholder: copy.placeholder,
    });

    useEffect(() => {
        setValue('personOrgGUID', selectedPersonProfile?.personOrgGUID ?? undefined);
    }, [selectedPersonProfile?.personOrgGUID, setValue]);

    return (
        <>
            <LinkPersonToHouseholdRoleFields />
            <FormSection title={copy.section} hasDivider>
                <PersonSelector />
                {errors.personOrgGUID && <Text $themeColor="negative">{errors.personOrgGUID.message?.toString()}</Text>}
                {personOrgType !== PersonOrgType.PERSON && (
                    <Text helper $themeColor="subtle">
                        Linking also adds its members (owners, trustees, beneficiaries, officers) to this household.
                    </Text>
                )}
            </FormSection>
        </>
    );
};
